// src/data/questions/privacyQuestions.ts
// Privacy Rule Compliance Questions (Q5-Q10)

import { Question } from '../../types';

export const privacyQuestions: Question[] = [
  {
    id: 'Q5',
    section: 'privacy',
    question: 'Do you have a current Notice of Privacy Practices (NPP)?',
    type: 'single',
    options: [
      { value: 'current', label: 'Yes, updated within last 3 years', description: '', points: 3 },
      { value: 'outdated', label: 'Yes, but outdated (3+ years old)', description: '', points: 1 },
      { value: 'no', label: 'No, we don\'t have one', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '§164.520 - Notice of Privacy Practices',
    required: true,
    weight: 2,
    tooltipContent: {
      plainEnglish: 'A Notice of Privacy Practices is your primary document for communicating how you handle patient data. It must be written in plain language, explain patients\' rights (access, amendment, accounting of disclosures), describe your uses of PHI, list any required authorizations, and detail how patients can file complaints. HIPAA requires you to provide the NPP at first encounter and update it whenever your practices change.',
      hipaaReference: '§164.520 - Notice of Privacy Practices',
      realWorldExample: 'A clinic discovers it hasn\'t updated its NPP in 5 years. During an OCR audit, the auditor notes that the NPP doesn\'t mention the clinic\'s use of a cloud EHR, doesn\'t explain how telemedicine works, and doesn\'t disclose that staff members can view records for research. The clinic receives a citation and must retrain staff.',
      estimatedCost: {
        small: '$500-2,000 (DIY template + legal review)',
        medium: '$2,000-5,000 (custom legal drafting)',
        large: '$5,000-15,000 (comprehensive legal review)'
      },
      implementationSteps: [
        'Review your current NPP or obtain a template from CMS/HHS',
        'Document all uses and disclosures of PHI in your organization',
        'Have legal counsel review and approve the document',
        'Print and distribute to all patients; post in waiting areas',
        'Update annually or when practices change'
      ],
      commonMistakes: [
        'Using an outdated template that doesn\'t reflect current practices',
        'Not updating when you add new services (telehealth, third-party apps)',
        'Failing to give the NPP to all patients and document receipt'
      ],
      resourceLinks: [
        { title: "HHS Notice of Privacy Practices", url: "https://www.hhs.gov/hipaa/for-professionals/privacy/guidance/privacy-practices-for-protected-health-information/index.html", type: "external" },
        { title: "Sample NPP Template", url: "https://www.hhs.gov/hipaa/for-professionals/privacy/guidance/model-notices-privacy-practices/index.html", type: "external" }
      ],
      timeToComplete: {
        small: '1-2 weeks',
        medium: '2-4 weeks',
        large: '4-8 weeks'
      }
    },
    investigationGuide: {
      whoToAsk: ['Compliance officer', 'Legal counsel', 'Privacy officer', 'Practice manager'],
      whereToLook: ['Patient intake forms', 'Website privacy policy', 'Patient portal', 'Waiting room postings'],
      howToVerify: 'Check if you have a current NPP document, when it was last updated, and if it reflects all current practices',
      whatToDocument: 'Document NPP version, last update date, distribution method, and patient acknowledgment process'
    }
  },
  {
    id: 'Q6',
    section: 'privacy',
    question: 'How do you handle patient authorization for uses/disclosures of PHI?',
    type: 'single',
    options: [
      { value: 'written', label: 'We obtain written authorization for all non-TPO uses', description: '', points: 3 },
      { value: 'verbal', label: 'We obtain verbal authorization', description: '', points: 1 },
      { value: 'none', label: 'We don\'t always obtain authorization', description: '', points: 0 },
      { value: 'unsure', label: 'Not sure what this means', description: '', points: 0 },
    ],
    regulation: '§164.508 - Authorization requirements',
    required: true,
    weight: 3,
    tooltipContent: {
      plainEnglish: 'HIPAA distinguishes between TPO (Treatment, Payment, Operations) and other uses/disclosures. You can use/disclose PHI for TPO without authorization. Any other use—research, marketing, selling data to third parties, quality improvement projects—requires written authorization. A proper authorization process means forms are HIPAA-compliant, staff are trained, authorizations are tracked, and you honor revocations.',
      hipaaReference: '§164.508 - Authorization Requirements; §164.502(b)(1) - TPO uses without authorization',
      realWorldExample: 'A clinic wants to share de-identified data with a research institution. Even though data is de-identified, they still need written authorization because the regulation requires authorization for "research" as a non-TPO use. A weight loss clinic conducts a "success stories" marketing campaign featuring before-and-after photos. Patients sign authorizations specifically for this marketing use.',
      estimatedCost: {
        small: '$500-1,500 (DIY forms)',
        medium: '$2,000-4,000 (custom legal + tracking)',
        large: '$5,000-15,000 (comprehensive system)'
      },
      implementationSteps: [
        'Create HIPAA-compliant authorization forms',
        'Train staff on TPO vs. non-TPO scenarios',
        'Implement authorization tracking system',
        'Establish process for honoring revocations'
      ],
      commonMistakes: [
        'Using verbal authorization instead of written',
        'Not tracking which patients authorized what uses',
        'Failing to honor authorization revocations'
      ],
      resourceLinks: [
        { title: "HHS Authorization Requirements", url: "https://www.hhs.gov/hipaa/for-professionals/privacy/guidance/authorization/index.html", type: "external" }
      ],
      timeToComplete: {
        small: '2-3 weeks',
        medium: '4-6 weeks',
        large: '6-12 weeks'
      }
    },
    investigationGuide: {
      whoToAsk: ['Compliance officer', 'Legal counsel', 'Privacy officer', 'Front desk staff'],
      whereToLook: ['Authorization forms', 'Patient files', 'Marketing materials', 'Research protocols'],
      howToVerify: 'Review if you have written authorization forms for non-TPO uses and if staff understand when to use them',
      whatToDocument: 'Document authorization form templates, tracking system, and staff training records'
    }
  },
  {
    id: 'Q7',
    section: 'privacy',
    question: 'Do you apply the "minimum necessary" standard when using/disclosing PHI?',
    type: 'single',
    options: [
      { value: 'documented', label: 'Yes, we have documented policies and train staff', description: '', points: 3 },
      { value: 'partial', label: 'Partially - we try but don\'t have formal policies', description: '', points: 2 },
      { value: 'no', label: 'No formal process', description: '', points: 0 },
      { value: 'unsure', label: 'What is "minimum necessary"?', description: '', points: 0 },
    ],
    regulation: '§164.502(b) - Minimum necessary',
    required: true,
    weight: 2,
    tooltipContent: {
      plainEnglish: 'The "minimum necessary" standard is a core privacy principle: when you use or disclose PHI, share only the information needed to accomplish the specific purpose. This requires role-based access control, documented policies, and staff training. You must be able to show OCR that you have a process to enforce minimum necessary, not just a vague statement that staff should "only access what they need."',
      hipaaReference: '§164.502(b) - Minimum necessary principle',
      realWorldExample: 'A clinic implements RBAC in their EHR: receptionists see only demographic and insurance info; clinicians see full records; billing sees insurance and diagnosis/procedure codes; IT staff sees only technical metadata (not clinical content). Each role has documented access levels.',
      estimatedCost: {
        small: '$5,000-20,000 (RBAC configuration)',
        medium: '$15,000-50,000 (comprehensive RBAC)',
        large: '$50,000-200,000 (enterprise RBAC)'
      },
      implementationSteps: [
        'Define roles and their minimum necessary access levels',
        'Configure RBAC in your EHR and systems',
        'Document access policies by role',
        'Train staff on their specific access rights',
        'Conduct quarterly access reviews'
      ],
      commonMistakes: [
        'Giving everyone access to everything',
        'Not conducting regular access reviews',
        'Employee snooping and curious staff accessing records they don\'t need'
      ],
      resourceLinks: [
        { title: "HHS Minimum Necessary Guidance", url: "https://www.hhs.gov/hipaa/for-professionals/privacy/guidance/minimum-necessary-requirement/index.html", type: "external" }
      ],
      timeToComplete: {
        small: '3-6 weeks',
        medium: '6-12 weeks',
        large: '12-24 weeks'
      }
    },
    investigationGuide: {
      whoToAsk: ['IT director', 'Security officer', 'EHR administrator', 'Compliance officer'],
      whereToLook: ['EHR access logs', 'User role definitions', 'Access control policies', 'Training records'],
      howToVerify: 'Review if you have role-based access controls configured and if staff only access PHI needed for their job',
      whatToDocument: 'Document role definitions, access levels, RBAC configuration, and access review schedule'
    }
  },
  {
    id: 'Q8',
    section: 'privacy',
    question: 'Can patients access their own health records within 30 days?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes, we have a documented process (electronic + paper)', description: '', points: 3 },
      { value: 'slow', label: 'Partially - takes longer than 30 days', description: '', points: 1 },
      { value: 'no', label: 'No established process', description: '', points: 0 },
      { value: 'na', label: 'Not applicable', description: '', points: 0 },
    ],
    regulation: '§164.524 - Right of access',
    required: true,
    weight: 3,
    tooltipContent: {
      plainEnglish: 'Patients have a fundamental right to access their medical records. HIPAA requires you to provide access within 30 days (or 60 days if you request an extension). Access must be available in both electronic format (if you maintain electronic records) and paper format. You need a documented process, ideally automated, to gather all relevant records and provide them to patients quickly.',
      hipaaReference: '§164.524 - Right of Access',
      realWorldExample: 'A clinic receives a patient\'s record access request on Monday. By Friday, they must compile: current EHR notes, lab results from the lab vendor, imaging reports from the imaging center, medication lists from the pharmacy, and care plans from the physical therapist. The clinic has a documented process with 24-hour turnaround agreements with vendors.',
      estimatedCost: {
        small: '$3,000-10,000 (patient portal setup)',
        medium: '$10,000-30,000 (comprehensive system)',
        large: '$30,000-100,000 (enterprise solution)'
      },
      implementationSteps: [
        'Implement patient portal for electronic access',
        'Create record compilation workflow',
        'Establish agreements with external vendors',
        'Train staff on fulfillment process',
        'Test end-to-end within 30-day timeline'
      ],
      commonMistakes: [
        'Taking longer than 30 days to fulfill requests',
        'Not providing electronic access when records are electronic',
        'Charging excessive fees for record copies'
      ],
      resourceLinks: [
        { title: "HHS Right of Access", url: "https://www.hhs.gov/hipaa/for-professionals/privacy/guidance/access/index.html", type: "external" }
      ],
      timeToComplete: {
        small: '4-8 weeks',
        medium: '8-16 weeks',
        large: '16-24 weeks'
      }
    },
    investigationGuide: {
      whoToAsk: ['EHR administrator', 'Medical records staff', 'IT director', 'Compliance officer'],
      whereToLook: ['Patient portal', 'Record request forms', 'Fulfillment procedures', 'Vendor agreements'],
      howToVerify: 'Test the process by requesting your own records and timing how long it takes',
      whatToDocument: 'Document record access process, fulfillment timeline, vendor agreements, and patient portal access'
    }
  },
  {
    id: 'Q9',
    section: 'privacy',
    question: 'Do you provide patients an accounting of PHI disclosures upon request?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes, we track all disclosures and can generate reports', description: '', points: 3 },
      { value: 'partial', label: 'Partially - we track some disclosures', description: '', points: 1 },
      { value: 'no', label: 'No tracking mechanism', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '§164.528 - Accounting of disclosures',
    required: true,
    weight: 2,
    tooltipContent: {
      plainEnglish: 'An accounting of disclosures is a record showing every time you\'ve disclosed a patient\'s PHI to a third party (excluding disclosures for treatment, payment, and operations). Patients can request this accounting at any time, and you must provide a list of: who received the information, when it was disclosed, what information was disclosed, and why. You must provide this within 30 days.',
      hipaaReference: '§164.528 - Accounting of Disclosures',
      realWorldExample: 'A compliant organization has audit logs showing: Dr. Johnson accessed the record on Jan 5 at 2pm (purpose: treatment); insurance company Aetna accessed the record on Jan 6 (purpose: payment review); research coordinator Smith accessed de-identified data on Jan 10 (purpose: quality improvement study). The patient request gets a detailed list with dates, recipients, and purposes.',
      estimatedCost: {
        small: '$10,000-50,000 (audit logging setup)',
        medium: '$25,000-100,000 (comprehensive logging)',
        large: '$100,000-500,000 (enterprise SIEM)'
      },
      implementationSteps: [
        'Implement comprehensive audit logging in all systems',
        'Configure logging to capture all PHI disclosures',
        'Create accounting report generation process',
        'Test report accuracy and completeness',
        'Train staff on disclosure tracking'
      ],
      commonMistakes: [
        'Not having audit logging robust enough to generate accurate accounting',
        'Missing disclosures to third parties',
        'Unable to produce accounting within 30 days'
      ],
      resourceLinks: [
        { title: "HHS Accounting of Disclosures", url: "https://www.hhs.gov/hipaa/for-professionals/privacy/guidance/accounting-of-disclosures/index.html", type: "external" }
      ],
      timeToComplete: {
        small: '4-8 weeks',
        medium: '8-16 weeks',
        large: '16-24 weeks'
      }
    },
    investigationGuide: {
      whoToAsk: ['IT director', 'Security officer', 'EHR administrator', 'Compliance officer'],
      whereToLook: ['Audit logs', 'Disclosure tracking systems', 'Third-party access logs', 'Vendor agreements'],
      howToVerify: 'Test if you can generate a complete accounting of disclosures for a sample patient',
      whatToDocument: 'Document audit logging configuration, disclosure tracking process, and report generation capability'
    }
  },
  {
    id: 'Q10',
    section: 'privacy',
    question: 'Do you disclose to patients when AI is used in their care?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes, we have clear disclosure procedures', description: '', points: 3 },
      { value: 'sometimes', label: 'Sometimes, depends on the use case', description: '', points: 1 },
      { value: 'no', label: 'No formal disclosure', description: '', points: 0 },
      { value: 'na', label: 'Not applicable - we don\'t use AI', description: '', points: 0 },
    ],
    regulation: 'State AI disclosure laws (CA AB 2013, NY S.7803)',
    required: false,
    conditionalOn: { questionId: 'Q4', requiredValue: 'ai' },
    weight: 2,
    tooltipContent: {
      plainEnglish: 'State AI laws (California\'s AB 2013, New York\'s S.7803, Colorado\'s AI Transparency Act, Illinois\' BIPA) now require transparency when AI/ML systems are used in healthcare decisions. You must disclose that an AI system is being used, how it influences care decisions, and often provide explanations of how the AI reached its conclusions. Patients must have the right to opt out of AI analysis if possible.',
      hipaaReference: 'California AB 2013, New York S.7803, Colorado SB 21-169, Illinois BIPA',
      realWorldExample: 'A clinic discloses: "When you come to our clinic, an AI system may help analyze your symptoms and suggest diagnoses. A clinician reviews the AI\'s recommendation before making final decisions. The AI is trained on medical literature and clinical data, but not on your specific records."',
      estimatedCost: {
        small: '$2,000-5,000 (disclosure forms + training)',
        medium: '$5,000-15,000 (comprehensive disclosure)',
        large: '$15,000-50,000 (enterprise AI governance)'
      },
      implementationSteps: [
        'Identify all AI systems used in clinical workflows',
        'Create patient disclosure forms and notices',
        'Update NPP to include AI transparency',
        'Train staff on AI disclosure requirements',
        'Implement opt-out mechanisms where feasible'
      ],
      commonMistakes: [
        'Using AI without disclosing to patients',
        'Not complying with state-specific AI transparency laws',
        'Failing to provide opt-out options'
      ],
      resourceLinks: [
        { title: "California AB 2013 AI Disclosure", url: "https://leginfo.legislature.ca.gov/faces/billTextClient.xhtml?bill_id=202320240AB2013", type: "external" }
      ],
      timeToComplete: {
        small: '2-4 weeks',
        medium: '4-8 weeks',
        large: '8-16 weeks'
      }
    },
    investigationGuide: {
      whoToAsk: ['Compliance officer', 'Legal counsel', 'AI/ML team', 'Clinical leadership'],
      whereToLook: ['AI system documentation', 'Patient consent forms', 'NPP', 'State regulations'],
      howToVerify: 'Review if you disclose AI use to patients and if disclosures comply with state laws',
      whatToDocument: 'Document all AI systems, disclosure procedures, patient consent, and opt-out mechanisms'
    }
  },
];
